import React, { forwardRef } from 'react';

import * as Styles from './styles';
import { TooltipV2 } from '../Tooltip/TooltipV2';
import { cn } from '../../lib/utils';

export const classes = {
  root: 'NEXYMultipleSwitch',
  section: 'NEXYMultipleSwitchSection',
};

interface Props {
  disabled?: boolean;
  switchNotAllowed?: boolean;
  onToggle?: (e: string) => void;
  sections: { id: string; text: string }[];
  style?: Record<string, unknown>;
  withTooltip?: boolean;
  current?: string;
  switchClassname?: string;
  sectionClassname?: string;
  /**
   * Rendered raw inside the track after the last section, OUTSIDE the section
   * click wiring — for a control that lives in the track but is not one of the
   * `sections` (the Optimize proposal's "By Label ▾" menu button).
   *
   * Deliberately not wrapped in `Styles.Section`: that styled-component's
   * `span { color }` rule would override whatever the control sets, and its
   * whole-div `onClick` would fire `onToggle` on every interaction with it.
   *
   * It must stay in the destructure below as well as in `Props`: the component is
   * `forwardRef<Props, any>` so TypeScript does not check the destructure, and
   * `...rest` is spread onto the styled div — a forgotten one renders it twice
   * and puts a React element on a DOM attribute.
   */
  trailing?: React.ReactNode;
}

const MultipleSwitch = forwardRef<Props, any>(function Switch(props, ref) {
  const {
    switchNotAllowed,
    onToggle,
    sections,
    current,
    initial,
    withTooltip = false,
    switchClassname,
    sectionClassname,
    trailing,
    ...rest
  } = props;

  const toggle = React.useCallback(
    (id: string) => {
      onToggle && onToggle(id);
    },
    [onToggle],
  );

  return (
    <Styles.Wrapper className={cn(classes.root, switchClassname)} notAllowed={switchNotAllowed} {...rest} ref={ref}>
      {sections.map(({ id, text }) => (
        <Styles.Section
          className={cn(classes.section, sectionClassname)}
          key={id}
          active={current ? current === id : initial}
          onClick={() => toggle(id)}
        >
          <span className={withTooltip ? 'max-w-64 truncate overflow-ellipsis' : ''}>
            {withTooltip ? (
              <TooltipV2 content={text} variant="dark">
                <div>{text}</div>
              </TooltipV2>
            ) : (
              text
            )}
          </span>
        </Styles.Section>
      ))}
      {trailing}
    </Styles.Wrapper>
  );
});

export default MultipleSwitch;
